import { useCallback, useEffect } from 'react';
import { toast } from 'sonner';

import { useTransactionStore } from '@/store/transaction';

import type { ChainAddress, ChainHash } from '@/utils/models';

import { TradeToastCrossChain } from './TradeToastCrossChain';
import { TradeToastSwap } from './TradeToastSwap';

const TRADE_TOAST_OPTIONS = {
  duration: Infinity,
  unstyled: true,
} as const;

type TradeToastProps = {
  walletAddress: ChainAddress;
  hash: ChainHash;
  chainId: number;
  toastId: string | number;
};

type ShowTradeToastParams = Omit<TradeToastProps, 'toastId'>;

export const showTradeToast = (params: ShowTradeToastParams) =>
  toast.custom((toastId) => <TradeToast {...params} toastId={toastId} />, TRADE_TOAST_OPTIONS);

export const TradeToast = ({ walletAddress, hash, chainId, toastId }: TradeToastProps) => {
  const transaction = useTransactionStore((state) =>
    state.transactions[walletAddress]?.[chainId]?.find(
      (tx) => tx.originalHash === hash || tx.hash === hash,
    ),
  );

  const onDismiss = useCallback(() => toast.dismiss(toastId), [toastId]);

  // Update internal height calculation of Sonner library to correctly stack multiple toasts
  const handleHeightChange = useCallback(() => {
    toast.custom(
      () => (
        <TradeToast walletAddress={walletAddress} hash={hash} chainId={chainId} toastId={toastId} />
      ),
      { ...TRADE_TOAST_OPTIONS, id: toastId },
    );
  }, [chainId, hash, toastId, walletAddress]);

  // Dismiss toast when transaction is removed from the store
  useEffect(() => {
    if (transaction == null) onDismiss();
  }, [transaction, onDismiss]);

  if (transaction == null) return null;

  const toastProps = { onDismiss, onHeightChange: handleHeightChange };

  if (transaction.transactionType === 'cross-chain') {
    return <TradeToastCrossChain transaction={transaction} {...toastProps} />;
  }

  return <TradeToastSwap transaction={transaction} {...toastProps} />;
};
